/**
 * Kill switch for the whole consent widget — banner, modal and the "Cookies"
 * footer link. Set to `false` to disable it; nothing else needs to change.
 *
 * While disabled the kernel reports every category as granted, so the things
 * consent gates (analytics, third-party embeds, "remember me") keep working as
 * they did before consent shipped. Reporting them as *denied* instead would
 * silently switch all of that off — see `getConsent` in `kernel.ts`.
 *
 * Disabling hides the banner but does not stop non-essential cookies being set,
 * which is not a defensible steady state in the EU. Short-term use only.
 */
export const CONSENT_ENABLED = true;

export const CONSENT_COOKIE_NAME = 'linkie_consent';

export const CONSENT_STORAGE_KEY = 'linkie_consent';

export const CONSENT_POLICY_VERSION = 1;

export const CONSENT_RECORD_VERSION = 1;

export const CONSENT_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

export const CONSENT_CATEGORY = {
  NECESSARY: 'necessary',
  FUNCTIONAL: 'functional',
  ANALYTICS: 'analytics',
  PERFORMANCE: 'performance',
  EMBEDS: 'embeds',
  MARKETING: 'marketing',
} as const;

export type ConsentCategory = (typeof CONSENT_CATEGORY)[keyof typeof CONSENT_CATEGORY];

export const OPTIONAL_CATEGORIES = [
  CONSENT_CATEGORY.FUNCTIONAL,
  CONSENT_CATEGORY.ANALYTICS,
  CONSENT_CATEGORY.EMBEDS,
  CONSENT_CATEGORY.MARKETING,
  CONSENT_CATEGORY.PERFORMANCE,
] as const;

export type OptionalConsentCategory = (typeof OPTIONAL_CATEGORIES)[number];

export const REQUIRES_PROMPT: ReadonlySet<ConsentCategory> = new Set([
  CONSENT_CATEGORY.EMBEDS,
  CONSENT_CATEGORY.MARKETING,
]);

export const NO_CONSENT_REQUIRED: ReadonlySet<ConsentCategory> = new Set([
  CONSENT_CATEGORY.ANALYTICS,
  CONSENT_CATEGORY.PERFORMANCE,
]);

export const CONSENT_REGIONS: ReadonlySet<string> = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
  'CH',
]);

const UNRESOLVED_COUNTRIES: ReadonlySet<string> = new Set(['XX', 'T1']);

export const inConsentRegion = (country: string | null | undefined): boolean => {
  if (!country) return true;

  const code = country.toUpperCase();
  if (UNRESOLVED_COUNTRIES.has(code)) return true;

  return CONSENT_REGIONS.has(code);
};

export const CONSENT_SURFACE = {
  PUBLIC_PAGE: 'public-page',
  APP: 'app',
  MARKETING: 'marketing',
  HELP: 'help',
} as const;

export type ConsentSurface = (typeof CONSENT_SURFACE)[keyof typeof CONSENT_SURFACE];

export const NO_CONSENT_SURFACES: ReadonlySet<ConsentSurface> = new Set([
  CONSENT_SURFACE.PUBLIC_PAGE,
  CONSENT_SURFACE.APP,
]);

export const CONSENT_METHOD = {
  BANNER: 'b',
  PREFERENCES: 'p',
  GPC: 'g',
  EMBED: 'e',
  INLINE: 'i',
} as const;

export type ConsentMethod = (typeof CONSENT_METHOD)[keyof typeof CONSENT_METHOD];

export const RECORD_KEY_BY_CATEGORY = {
  [CONSENT_CATEGORY.FUNCTIONAL]: 'f',
  [CONSENT_CATEGORY.ANALYTICS]: 'a',
  [CONSENT_CATEGORY.PERFORMANCE]: 'r',
  [CONSENT_CATEGORY.EMBEDS]: 'e',
  [CONSENT_CATEGORY.MARKETING]: 'm',
} as const satisfies Record<OptionalConsentCategory, string>;

export type ConsentRecordKey = (typeof RECORD_KEY_BY_CATEGORY)[OptionalConsentCategory];

export interface ConsentRecord {
  v: number;
  p: number;
  c: Partial<Record<ConsentRecordKey, 0 | 1>>;
  t: number;
  id: string;
  via: ConsentMethod;
}

export interface ConsentSnapshot {
  readonly policyVersion: number;
  readonly categories: Readonly<Record<ConsentCategory, boolean>>;
  readonly timestamp: number;
  readonly id: string;
  readonly method: ConsentMethod;
}

export const CONSENT_EVENT = 'linkie:consent';

export const BLOCKED_ATTR = 'data-linkie-consent';

export const OPEN_PREFERENCES_ATTR = 'data-linkie-consent-open';
export const OPEN_PREFERENCES_HASH = '#cookie-settings';

export const SNIPPET_SIG = 'lkc1';
